import React, { useState, useEffect, useMemo } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { useGlobalContext } from "../Config";
import { TabView, TabPanel } from "primereact/tabview";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import { Dialog } from "primereact/dialog";
import { RadioButton } from "primereact/radiobutton"; // (opcional si prefieres radios)
import {
  countryOptions,
  countryOptionTemplate,
} from "../utilities/CountryOptions";
import {
  phoneCodeOptions,
  phoneCodeOptionTemplate,
} from "../utilities/PhoneCodeOptions";

// === imports adicionales para la UI de contactos ===
import { Toolbar } from "primereact/toolbar";
import { SelectButton } from "primereact/selectbutton";
import { Sidebar } from "primereact/sidebar";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import { Skeleton } from "primereact/skeleton";

const Contacto = () => {
  const navigate = useNavigate();
  const { baseUrl } = useGlobalContext?.() || {};

  // ------- estados base -------
  const [contacts, setContacts] = useState([]); // <- llena desde tu API
  const [loading, setLoading] = useState(false); // <- ajusta mientras llamas a la API
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [view, setView] = useState("kanban"); // "kanban" | "lista"
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [tipoFilter, setTipoFilter] = useState("");
  const [contactoRut, setContactoRut] = useState("");
  const [tipoDraft, setTipoDraft] = useState("");
  const [contactoRutDraft, setContactoRutDraft] = useState("");
  const [showDireccion, setShowDireccion] = useState(false);
  const [showCorreo, setShowCorreo] = useState(false);
  const [showTelefono, setShowTelefono] = useState(false);
  const [direccionQuery, setDireccionQuery] = useState("");
  const [direccionResultados, setDireccionResultados] = useState([]);
  const today = useMemo(() => new Date(), []);
  const [direccionSeleccionada, setDireccionSeleccionada] = useState(null);
  const [loadingDireccion, setLoadingDireccion] = useState(false);
  const [rutChecking, setRutChecking] = useState(false);
  const [filtersApplying, setFiltersApplying] = useState(false);
  const [rutStatus, setRutStatus] = useState(null);
  const [rutExists, setRutExists] = useState(false);
  const [rutMessage, setRutMessage] = useState("");
  const [tiposDireccion, setTiposDireccion] = useState([]);
  const [tipoDireccionId, setTipoDireccionId] = useState(null);
  const [loadingTiposDireccion, setLoadingTiposDireccion] = useState(false);
  const [phoneCountryCode, setPhoneCountryCode] = useState("+56");
  const DEFAULT_COUNTRY = "CL";
  const [countryAutoLoading, setCountryAutoLoading] = useState(false);
  const [countryAutoError, setCountryAutoError] = useState(false);

  const [nacionalidadTouched, setNacionalidadTouched] = useState(false);

  // --------- Fetch inicial ---------

  const displayName = (it) => {
    if (it.tipo === "JURIDICA") return it.razonSocial || it.rut || "—";
    if (it.tipo === "LEAD") return it.descripcionLead || it.rut || "—";

    const partes = [
      it.primerNombre,
      it.segundoNombre,
      it.apellidoPaterno,
      it.apellidoMaterno,
    ].filter(Boolean);
    return partes.length ? partes.join(" ") : it.rut || "—";
  };

  const fetchPersonas = async (opts = {}) => {
    try {
      setLoading(true);
      const t = (opts.tipo ?? tipoFilter) || "";
      const r = (opts.contactoRut ?? contactoRut)?.trim() || "";
      const q = (opts.q ?? "").trim();

      const params = new URLSearchParams();
      if (t) params.set("tipo", t); // "NATURAL" | "JURIDICA"

      if (r) {
        const rutLimpio = cleanRut(r);
        params.set("contactosDeRut", rutLimpio);
      }

      if (q) {
        params.set("q", q);
      }

      const res = await fetch(
        `${baseUrl}personas${params.toString() ? `?${params.toString()}` : ""}`,
        {
          method: "GET",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!res.ok) throw new Error(await res.text());

      const data = await res.json();
      const items = Array.isArray(data.items) ? data.items : [];

      const mapped = items.map((it) => ({
        id: it.id,
        rut: it.rut,
        tipo: it.tipo,
        name: displayName(it),
        email: it.email || "",
        phone: it.telefono || it.phone || "",
        location: it.ubicacion || it.location || "",
        raw: it,
      }));

      setContacts(mapped);
    } catch (e) {
      console.error(e);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  // ------- efecto inicial-------

  useEffect(() => {
    // si está vacío, carga la lista normal
    if (!debounced) {
      fetchPersonas();
      return;
    }

    if (debounced.length < 2) return;

    fetchPersonas({ q: debounced });
  }, [debounced, tipoFilter]);

  useEffect(() => {
    if (!baseUrl) return;
    if (!Array.isArray(contacts) || contacts.length === 0) return;

    // precarga fotos para los visibles
    contacts.forEach((c) => {
      if (c?.id) fetchFotoPerfil(c.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, baseUrl]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!showDireccion) return;
    if (!direccionQuery.trim()) {
      setDireccionResultados([]);
      return;
    }

    const t = setTimeout(async () => {
      try {
        setLoadingDireccion(true);
        const res = await fetch(
          `${baseUrl}direcciones/buscar?q=${encodeURIComponent(
            direccionQuery
          )}`,
          {
            credentials: "include",
            headers: { "Content-Type": "application/json" },
          }
        );

        if (res.ok) {
          const data = await res.json();
          setDireccionResultados(data.direcciones || []);
        } else {
          setDireccionResultados([]);
        }
      } catch (e) {
        console.error("Error buscando direcciones:", e);
        setDireccionResultados([]);
      } finally {
        setLoadingDireccion(false);
      }
    }, 400);

    return () => clearTimeout(t);
  }, [direccionQuery, showDireccion, baseUrl]);

  useEffect(() => {
    if (!showDireccion || !baseUrl) return;

    let abort = false;

    (async () => {
      try {
        setLoadingTiposDireccion(true);
        const resp = await fetch(`${baseUrl}direcciones/tipos`, {
          credentials: "include",
        });
        if (!resp.ok) throw new Error(await resp.text());

        const data = await resp.json();
        const opts = (data.items || []).map((t) => ({
          label: t.descripcion || t.codigo,
          value: t.id,
        }));

        if (!abort) {
          setTiposDireccion(opts);
          // si no hay nada seleccionado, dejamos el primero por defecto
          setTipoDireccionId((prev) => prev ?? opts[0]?.value ?? null);
        }
      } catch (e) {
        console.error("Error cargando tipos de dirección:", e);
        if (!abort) {
          setTiposDireccion([]);
          setTipoDireccionId(null);
        }
      } finally {
        if (!abort) setLoadingTiposDireccion(false);
      }
    })();

    return () => {
      abort = true;
    };
  }, [showDireccion, baseUrl]);

  const filtered = contacts;

  // ===== Fotos de perfil (cache por personaId) =====
  const [fotoByPersonaId, setFotoByPersonaId] = useState({}); // { [id]: dataUrl | null }
  const [fotoLoadingByPersonaId, setFotoLoadingByPersonaId] = useState({}); // { [id]: boolean }

  const buildFotoDataUrl = (tipo, archivo) => {
    if (!tipo || !archivo) return null;

    const mime = String(tipo)
      .replace(/;base64$/i, "")
      .replace(/\/base64$/i, "");
    return `data:${mime};base64,${archivo}`;
  };

  const fetchFotoPerfil = async (personaId) => {
    if (!personaId) return;

    // si ya está en cache (incluso null), no refetch
    if (Object.prototype.hasOwnProperty.call(fotoByPersonaId, personaId))
      return;

    setFotoLoadingByPersonaId((p) => ({ ...p, [personaId]: true }));

    try {
      const resp = await fetch(`${baseUrl}persona/fotoperfil/${personaId}`, {
        credentials: "include",
      });

      if (!resp.ok) {
        setFotoByPersonaId((p) => ({ ...p, [personaId]: null }));
        return;
      }

      const data = await resp.json();
      const src = buildFotoDataUrl(data?.tipo, data?.archivo);

      setFotoByPersonaId((p) => ({ ...p, [personaId]: src || null }));
    } catch (e) {
      console.error("Error cargando foto:", e);
      setFotoByPersonaId((p) => ({ ...p, [personaId]: null }));
    } finally {
      setFotoLoadingByPersonaId((p) => ({ ...p, [personaId]: false }));
    }
  };

  // geolocalizacion con ip:
  const guessCountryByIp = async () => {
    try {
      setCountryAutoLoading(true);
      setCountryAutoError(false);

      // ipapi.co
      const r = await fetch("https://ipapi.co/json/");
      if (!r.ok) throw new Error("ipapi error");

      const data = await r.json();
      const cc = (data?.country_code || "").toUpperCase();

      // si vino algo tipo "CL"
      if (cc && cc.length === 2) return cc;

      throw new Error("no country_code");
    } catch (e) {
      console.warn("No se pudo inferir país por IP:", e);
      setCountryAutoError(true);
      return DEFAULT_COUNTRY;
    } finally {
      setCountryAutoLoading(false);
    }
  };

  // ------- dialog añadir contacto -------
  const [addDialog, setAddDialog] = useState(false);
  const [personType, setPersonType] = useState("natural");
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    rut: "",
    razonSocial: "",
    primerNombre: "",
    segundoNombre: "",
    apellidoPaterno: "",
    apellidoMaterno: "",
    fechaNacimiento: null,
    genero: null,
    nacionalidad: "CL",
    etiquetaPersona: "",
    descripcionLead: "",
  });

  const [dirForm, setDirForm] = useState({
    calle: "",
    comuna: "",
    ciudad: "",
    pais: "",
  });

  const [emailForm, setEmailForm] = useState({
    email: "",
    etiqueta: "",
  });

  const [telForm, setTelForm] = useState({
    numero: "",
    etiqueta: "",
  });
  const [errors, setErrors] = useState({});

  const generoOptions = [
    { label: "Masculino", value: "M" },
    { label: "Femenino", value: "F" },
    { label: "Otro", value: "OTRO" },
  ];

  const resetForm = () => {
    setPersonType("natural");
    setForm({
      rut: "",
      razonSocial: "",
      primerNombre: "",
      segundoNombre: "",
      apellidoPaterno: "",
      apellidoMaterno: "",
      fechaNacimiento: null,
      genero: null,
      nacionalidad: "CL",
      etiquetaPersona: "",
      descripcionLead: "",
    });
    setErrors({});
    setShowDireccion(false);
    setShowCorreo(false);
    setShowTelefono(false);

    setDireccionQuery("");
    setDireccionResultados([]);
    setDireccionSeleccionada(null);

    setEmailForm({ email: "", etiqueta: "" });
    setTelForm({ numero: "", etiqueta: "" });
    setPhoneCountryCode("+56");
    setTiposDireccion([]);
    setTipoDireccionId(null);
    setLoadingTiposDireccion(false);
    setNacionalidadTouched(false);
    setCountryAutoError(false);
    setCountryAutoLoading(false);
  };

  const abrirDialog = async () => {
    resetForm();
    setAddDialog(true);

    if (!nacionalidadTouched) {
      const cc = await guessCountryByIp();
      setForm((p) => ({ ...p, nacionalidad: cc || DEFAULT_COUNTRY }));
    }
  };

  const cerrarDialog = () => {
    setAddDialog(false);
  };

  // simple helper
  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // ==== Helpers para RUT ====
  const cleanRut = (rut) =>
    (rut || "").replace(/\./g, "").replace(/-/g, "").toUpperCase();

  const formatRut = (raw = "") => {
    let clean = cleanRut(raw);
    if (!clean) return "";

    const dv = clean.slice(-1);
    let body = clean.slice(0, -1);

    if (!/^\d+$/.test(body)) return raw;

    let out = "";
    while (body.length > 3) {
      out = "." + body.slice(-3) + out;
      body = body.slice(0, -3);
    }
    out = body + out;

    return `${out}-${dv}`;
  };

  const isValidRut = (rut) => {
    const clean = cleanRut(rut);
    if (!clean || clean.length < 2) return false;

    const body = clean.slice(0, -1);
    const dv = clean.slice(-1);

    if (!/^\d+$/.test(body)) return false;

    let sum = 0;
    let mult = 2;

    for (let i = body.length - 1; i >= 0; i--) {
      sum += parseInt(body[i], 10) * mult;
      mult = mult === 7 ? 2 : mult + 1;
    }

    const rest = 11 - (sum % 11);
    const dvExpected = rest === 11 ? "0" : rest === 10 ? "K" : String(rest);

    return dv === dvExpected;
  };

  // Validación requerida según tipo
  const validate = () => {
    const e = {};
    if (!personType) e.personType = "Seleccione el tipo de persona.";

    if (personType !== "lead") {
      if (!form.rut?.trim()) {
        e.rut = "El RUT es obligatorio.";
      } else if (!isValidRut(form.rut)) {
        e.rut = "El RUT ingresado no es válido.";
      } else if (rutExists) {
        e.rut = "El RUT ingresado ya se encuentra registrado.";
      }
    }

    if (personType === "juridica") {
      if (!form.razonSocial?.trim())
        e.razonSocial = "La razón social es obligatoria.";
    }

    if (personType === "natural") {
      if (!form.primerNombre?.trim()) e.primerNombre = "Obligatorio.";
      if (!form.apellidoPaterno?.trim()) e.apellidoPaterno = "Obligatorio.";
      if (!form.apellidoMaterno?.trim()) e.apellidoMaterno = "Obligatorio.";
      if (!form.fechaNacimiento) {
        e.fechaNacimiento = "Obligatorio.";
      } else if (form.fechaNacimiento > today) {
        e.fechaNacimiento = "La fecha de nacimiento no puede ser futura.";
      }
      if (!form.genero) e.genero = "Obligatorio.";
    }

    if (personType === "lead") {
      if (!form.descripcionLead?.trim())
        e.descripcionLead = "La descripción del lead es obligatoria.";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const formatRutInput = (value) => formatRut(value);

  // --------- Guardar nuevo contacto ------------
  const handleSave = async (redirectToDetail = false) => {
    if (saving) return;
    if (!validate()) return;

    setSaving(true);
    const fechaISODate = form.fechaNacimiento
      ? DateTime.fromJSDate(form.fechaNacimiento).toISODate()
      : null;

    let payload;

    if (personType === "juridica") {
      payload = {
        tipo: "juridica",
        rut: cleanRut(form.rut.trim()),
        razonSocial: form.razonSocial.trim(),
        nacionalidad: form.nacionalidad,
      };
    } else if (personType === "natural") {
      payload = {
        tipo: "natural",
        rut: cleanRut(form.rut.trim()),
        primerNombre: form.primerNombre.trim(),
        segundoNombre: form.segundoNombre?.trim() || "",
        apellidoPaterno: form.apellidoPaterno.trim(),
        apellidoMaterno: form.apellidoMaterno.trim(),
        fechaNacimiento: fechaISODate,
        genero: form.genero,
        nacionalidad: form.nacionalidad,
      };
    } else if (personType === "lead") {
      payload = {
        tipo: "lead",
        nacionalidad: form.nacionalidad,
        descripcionLead: form.descripcionLead?.trim() || "",
      };
    }
    try {
      const resp = await fetch(`${baseUrl}persona/agregar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!resp.ok) throw new Error(await resp.text());

      const data = await resp.json();
      const personaId = data.id;

      // === guardar direcciones/email/telefono ===
      await saveAdicionales(personaId);

      // refrescar tabla de contactos en esta página
      await fetchPersonas();

      // cerrar el diálogo
      cerrarDialog();

      // si el usuario eligió "Guardar y continuar", lo llevamos a la ficha
      if (redirectToDetail && personaId) {
        navigate(`/contacto/${personaId}`);
      }
    } catch (err) {
      console.error("Error al crear persona:", err);
    } finally {
      setSaving(false);
    }
  };

  // ================= GUARDAR DATOS ADICIONALES =================

  const saveAdicionales = async (personaId) => {
    const hoy = DateTime.now().toISODate(); // yyyy-MM-dd

    try {
      // ======= Dirección =======
      if (showDireccion && direccionSeleccionada) {
        const d = direccionSeleccionada;

        const payloadDir = {
          calle: d.calle || "",
          numero: d.numero || "",
          comuna: d.comuna || "",
          ciudad: d.ciudad || "",
          region: d.region || "",
          pais: d.pais || "",
          direccion: d.direccion || "",
          postal: d.postal || "",
          lat: d.lat ?? null,
          lng: d.lng ?? null,
          raw: JSON.stringify(d),
          tipoDireccionId: tipoDireccionId || null,
          principal: true,
          vigente: true,
          vigenteDesde: hoy,
        };

        const resDir = await fetch(
          `${baseUrl}direcciones/agregar/${personaId}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(payloadDir),
          }
        );

        if (!resDir.ok) {
          console.error("Error al guardar dirección");
          console.error(await resDir.text());
        }
      }

      // ======= Correo =======
      if (showCorreo && emailForm.email?.trim()) {
        const payloadEmail = {
          email: emailForm.email.trim(),
          etiqueta: emailForm.etiqueta || "",
          principal: true,
          vigente: true,
          vigenteDesde: hoy,
        };

        const resEmail = await fetch(`${baseUrl}correo/agregar/${personaId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payloadEmail),
        });

        if (!resEmail.ok) {
          console.error("Error al guardar correo");
          console.error(await resEmail.text());
        }
      }

      // ======= Teléfono =======
      if (showTelefono && telForm.numero?.trim()) {
        const localNumber = telForm.numero.trim();
        const fullPhone =
          phoneCountryCode && localNumber
            ? `${phoneCountryCode} ${localNumber}`
            : localNumber;

        const payloadTel = {
          telefono: fullPhone,
          etiqueta: telForm.etiqueta || "",
          principal: true,
          vigente: true,
          vigenteDesde: hoy,
        };

        const resTel = await fetch(`${baseUrl}telefono/agregar/${personaId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payloadTel),
        });

        if (!resTel.ok) {
          console.error("Error al guardar teléfono");
          console.error(await resTel.text());
        }
      }
    } catch (err) {
      console.error("Error guardando datos adicionales:", err);
    }
  };

  const viewOptions = [
    { label: "Kanban", value: "kanban", icon: "pi pi-th-large" },
    { label: "Lista", value: "lista", icon: "pi pi-list" },
  ];

  // -------- Helpers filtros ---------

  const applyFilters = async () => {
    try {
      const params = new URLSearchParams();

      if (tipoFilter && tipoFilter !== "todos") {
        params.set("tipo", tipoFilter);
      }

      const rutFiltro = contactoRut.trim();
      if (rutFiltro) {
        const rutLimpio = cleanRut(rutFiltro);
        params.set("contactosDeRut", rutLimpio);
      }

      const url = `${baseUrl}personas${params.toString() ? `?${params}` : ""}`;
      setLoading(true);
      const res = await fetch(url, { credentials: "include" });
      const data = await res.json();

      const mapped = (data.items || []).map((p) => {
        let name;
        if (p.tipo === "NATURAL") {
          name = [
            p.primerNombre,
            p.segundoNombre,
            p.apellidoPaterno,
            p.apellidoMaterno,
          ]
            .filter(Boolean)
            .join(" ");
        } else if (p.tipo === "JURIDICA") {
          name = p.razonSocial || p.rut;
        } else if (p.tipo === "LEAD") {
          name = p.descripcionLead || p.rut;
        }

        return {
          id: p.id,
          rut: p.rut,
          tipo: p.tipo,
          name,
          email: p.email,
          phone: p.phone,
          location: p.location,
        };
      });

      setContacts(mapped);
      setFiltersOpen(false);
    } catch (e) {
      console.error("Error al cargar personas:", e);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setTipoFilter("todos");
    setContactoRut("");
  };

  const abrirFiltros = () => {
    setTipoDraft(tipoFilter);
    setContactoRutDraft(contactoRut);
    setFiltersOpen(true);
  };

  // ------- Botón añadir -------

  return (
    <div className="p-4 md:p-5">
      <div className="text-3xl md:text-4xl font-semibold mb-3">CONTACTOS</div>

      <div className="rounded-2xl mb-3 w-full bg-white p-3 shadow-1">
        <div className="flex flex-wrap items-center gap-2">
          {/* Botón Añadir */}
          <Button
            label="Añadir Contacto"
            icon="pi pi-plus"
            severity="primary"
            className="flex-none"
            onClick={abrirDialog}
          />

          {/* Búsqueda */}
          <span className="p-input-icon-left flex-1 min-w-[180px]">
            <i className="pi pi-search" />
            <InputText
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ingrese"
              className="w-full"
            />
          </span>

          {/* Toggle vista */}
          <SelectButton
            value={view}
            onChange={(e) => setView(e.value)}
            options={viewOptions}
            allowEmpty={false}
            itemTemplate={(option) => (
              <div className="flex justify-center items-center w-full">
                <i className={`${option.icon} text-lg`} />
              </div>
            )}
            className="flex-none"
          />

          {/* Botón filtros */}
          <Button
            label="Filtros"
            icon="pi pi-sliders-h"
            outlined
            onClick={abrirFiltros}
            className="flex-none"
          />
        </div>
      </div>

      <Divider className="my-5" />

      {loading && (
        <div className="flex items-center gap-2 mb-3 text-sm text-color-secondary">
          <i className="pi pi-spin pi-spinner" />
          Cargando contactos...
        </div>
      )}

      {view === "lista" ? (
        <div className="card">
          <DataTable
            value={filtered}
            loading={loading}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 20, 50, 100]}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
            stripedRows
            emptyMessage="Sin resultados"
            className="rounded-2xl overflow-hidden text-sm md:text-base"
            tableStyle={{ minWidth: "100%" }}
            responsiveLayout="stack"
            breakpoint="768px" // se apila antes en móviles
            onRowClick={(e) => navigate(`/contacto/${e.data?.id || ""}`)}
          >
            <Column field="name" header="Nombre" sortable />
            <Column field="rut" header="RUT" sortable />
            <Column field="tipo" header="Tipo" sortable />
          </DataTable>
        </div>
      ) : (
        <div className="grid grid-nogutter">
          {loading
            ? Array.from({ length: 9 }).map((_, i) => (
                <div key={i} className="col-12 md:col-4 mb-3">
                  <Card className="h-full cursor-pointer transition-duration-150 transition-ease-in-out hover:shadow-3">
                    <div className="flex items-center gap-3">
                      <div className="surface-200 w-5rem h-5rem border-circle" />
                      <div className="flex-1">
                        <div
                          className="mb-2 surface-200"
                          style={{ height: "1.2rem", width: "80%" }}
                        />
                        <div
                          className="mb-2 surface-200"
                          style={{ height: "1rem", width: "60%" }}
                        />
                        <div
                          className="surface-200"
                          style={{ height: "1rem", width: "40%" }}
                        />
                      </div>
                    </div>
                  </Card>
                </div>
              ))
            : filtered.map((c) => (
                <div key={c.id} className="col-12 md:col-4 mb-3">
                  <Card
                    onClick={() => navigate(`/contacto/${c.id || ""}`)}
                    className="h-full cursor-pointer transition-duration-150 transition-ease-in-out hover:shadow-3"
                  >
                    <div className="flex gap-3">
                      <div className="flex items-start">
                        <div className="flex items-start">
                          <div className="w-5rem h-5rem border-circle overflow-hidden surface-200 flex align-items-center justify-content-center">
                            {fotoLoadingByPersonaId[c.id] ? (
                              <Skeleton shape="circle" size="5rem" />
                            ) : fotoByPersonaId[c.id] ? (
                              <img
                                src={fotoByPersonaId[c.id]}
                                alt={c.name || "Foto perfil"}
                                className="w-full h-full"
                                style={{ objectFit: "cover" }}
                                onError={() =>
                                  setFotoByPersonaId((p) => ({
                                    ...p,
                                    [c.id]: null,
                                  }))
                                }
                              />
                            ) : (
                              <i className="pi pi-user text-2xl text-color-secondary" />
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="text-lg font-medium mb-1">
                          {c.name || "—"}
                        </div>
                        <div className="text-sm text-color-secondary">
                          <span className="font-medium">RUT: </span>
                          {c.rut || "—"}
                        </div>
                        <div className="text-sm text-color-secondary">
                          <span className="font-medium">Tipo: </span>
                          {c.tipo || "—"}
                        </div>
                        {c.email && (
                          <div className="text-sm text-color-secondary">
                            {c.email}
                          </div>
                        )}
                        {c.phone && (
                          <div className="text-sm text-color-secondary">
                            {c.phone}
                          </div>
                        )}
                        {c.location && (
                          <div className="text-sm text-color-secondary">
                            {c.location}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                </div>
              ))}
        </div>
      )}

      {/* Sidebar de filtros */}
      <Sidebar
        visible={filtersOpen}
        position="right"
        onHide={() => setFiltersOpen(false)}
        header="Filtros"
        className="w-full sm:w-22rem"
      >
        <div className="flex flex-column gap-4">
          {/* Tipo */}
          <div>
            <label className="block text-sm font-medium mb-2">Tipo</label>
            <div className="flex flex-column gap-2">
              <div className="flex align-items-center gap-2">
                <RadioButton
                  inputId="tipoTodos"
                  name="tipo"
                  value="todos"
                  onChange={(e) => setTipoFilter(e.value)}
                  checked={tipoFilter === "todos"}
                />
                <label htmlFor="tipoTodos">Todos</label>
              </div>
              <div className="flex align-items-center gap-2">
                <RadioButton
                  inputId="tipoNatural"
                  name="tipo"
                  value="natural"
                  onChange={(e) => setTipoFilter(e.value)}
                  checked={tipoFilter === "natural"}
                />
                <label htmlFor="tipoNatural">Naturales</label>
              </div>
              <div className="flex align-items-center gap-2">
                <RadioButton
                  inputId="tipoJuridica"
                  name="tipo"
                  value="juridica"
                  onChange={(e) => setTipoFilter(e.value)}
                  checked={tipoFilter === "juridica"}
                />
                <label htmlFor="tipoJuridica">Jurídicas</label>
              </div>
              <div className="flex align-items-center gap-2">
                <RadioButton
                  inputId="tipoLead"
                  name="tipo"
                  value="lead"
                  onChange={(e) => setTipoFilter(e.value)}
                  checked={tipoFilter === "lead"}
                />
                <label htmlFor="tipoLead">Leads</label>
              </div>
            </div>
          </div>

          {/* Contacto de (RUT) */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Contacto de (RUT)
            </label>
            <InputText
              value={contactoRut}
              onChange={(e) => {
                const formatted = formatRut(e.target.value);
                setContactoRut(formatted);
              }}
              placeholder="12.345.678-9"
              className="w-full"
            />
          </div>

          {/* Footer fijo con acciones */}
          <div className="sticky bottom-0 left-0 right-0 pt-3 mt-2 border-top-1 surface-border bg-surface-0">
            <div className="flex justify-content-between gap-2">
              <Button
                label="Limpiar"
                icon="pi pi-filter-slash"
                outlined
                className="w-6"
                onClick={clearFilters}
              />
              <Button
                label="Aplicar"
                icon="pi pi-check"
                className="w-6"
                onClick={applyFilters}
              />
            </div>
          </div>
        </div>
      </Sidebar>

      {/* Dialog Añadir Contacto*/}

      <Dialog
        header="Nuevo contacto"
        visible={addDialog}
        style={{ width: "70rem", maxWidth: "95vw" }}
        modal
        className="p-fluid"
        onHide={cerrarDialog}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              label="Cancelar"
              outlined
              onClick={cerrarDialog}
              disabled={saving}
            />

            <Button
              label="Guardar y salir"
              icon="pi pi-check"
              severity="primary"
              onClick={() => handleSave(false)}
              loading={saving}
              disabled={saving}
            />

            <Button
              label="Guardar y continuar"
              icon="pi pi-arrow-right"
              severity="success"
              onClick={() => handleSave(true)}
              loading={saving}
              disabled={saving}
            />
          </div>
        }
      >
        {/* Tipo de persona */}
        <div className="mb-3">
          <label className="block text-sm font-medium mb-2">
            Tipo de persona <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-4 items-center">
            <div className="flex items-center gap-2">
              <RadioButton
                inputId="pnatural"
                name="personType"
                value="natural"
                onChange={(e) => setPersonType(e.value)}
                checked={personType === "natural"}
              />
              <label htmlFor="pnatural">Persona natural</label>
            </div>
            <div className="flex items-center gap-2">
              <RadioButton
                inputId="pjuridica"
                name="personType"
                value="juridica"
                onChange={(e) => setPersonType(e.value)}
                checked={personType === "juridica"}
              />
              <label htmlFor="pjuridica">Persona jurídica</label>
            </div>
            <div className="flex items-center gap-2">
              <RadioButton
                inputId="plead"
                name="personType"
                value="lead"
                onChange={(e) => setPersonType(e.value)}
                checked={personType === "lead"}
              />
              <label htmlFor="plead">Lead</label>
            </div>
          </div>
          {errors.personType && (
            <small className="p-error">{errors.personType}</small>
          )}
        </div>

        <div className="grid formgrid">
          {/* País / Nacionalidad */}
          <div className="field col-12 md:col-6">
            <label className="block text-sm font-medium mb-2">
              País / Nacionalidad <span className="text-red-500">*</span>
            </label>

            <Dropdown
              value={form.nacionalidad}
              onChange={(e) => {
                setNacionalidadTouched(true);
                setField("nacionalidad", e.value);
              }}
              options={countryOptions}
              optionLabel="label"
              optionValue="code"
              placeholder="Seleccione país"
              itemTemplate={countryOptionTemplate}
              valueTemplate={countryOptionTemplate}
              className={errors.nacionalidad ? "p-invalid w-full" : "w-full"}
            />

            {errors.nacionalidad && (
              <small className="p-error">{errors.nacionalidad}</small>
            )}
          </div>
          {/* RUT */}
          {personType !== "lead" && (
            <div className="field col-12 md:col-6">
              <label className="block text-sm font-medium mb-2">
                RUT / Identificador <span className="text-red-500">*</span>
              </label>

              <InputText
                value={form.rut}
                onChange={(e) => {
                  const formatted = formatRutInput(e.target.value);
                  setField("rut", formatted);

                  // al escribir, limpio estados para no mostrar mensajes viejos
                  setErrors((prev) => ({ ...prev, rut: undefined }));
                  setRutStatus(null);
                  setRutExists(false);
                  setRutMessage("");
                }}
                onBlur={async () => {
                  const v = form.rut?.trim();
                  if (!v) {
                    setRutStatus(null);
                    setRutExists(false);
                    setRutMessage("");
                    return;
                  }

                  // 1) validar módulo 11
                  if (!isValidRut(v)) {
                    setRutStatus("invalid");
                    setRutExists(false);
                    setRutMessage("");
                    setErrors((prev) => ({
                      ...prev,
                      rut: "El RUT ingresado no es válido.",
                    }));
                    return;
                  }

                  // RUT válido por módulo 11
                  setRutChecking(true);
                  setRutStatus("valid");
                  setErrors((prev) => ({ ...prev, rut: undefined }));

                  // 2) consultar si ya existe en la base de datos
                  try {
                    const resp = await fetch(
                      `${baseUrl}personas/buscar?rut=${encodeURIComponent(v)}`,
                      { credentials: "include" }
                    );

                    if (!resp.ok) {
                      // si hay error de backend, no bloqueamos, solo limpiamos mensaje
                      setRutExists(false);
                      setRutMessage("");
                      return;
                    }

                    const data = await resp.json();
                    const exists =
                      Array.isArray(data.items) && data.items.length > 0;

                    if (exists) {
                      setRutExists(true);
                      setRutMessage("Este RUT ya se encuentra registrado.");
                      setErrors((prev) => ({
                        ...prev,
                        rut: "El RUT ingresado ya se encuentra registrado.",
                      }));
                    } else {
                      setRutExists(false);
                      setRutMessage("RUT válido y disponible.");
                    }
                  } catch (err) {
                    console.error("Error verificando existencia de RUT:", err);
                    // ante error, no asumimos nada, solo limpiamos mensaje extra
                    setRutExists(false);
                    setRutMessage("");
                  } finally {
                    setRutChecking(false);
                  }
                }}
                placeholder="12.345.678-9"
                className={errors.rut ? "p-invalid w-full" : "w-full"}
              />

              {/* Mensajes debajo del input */}
              {errors.rut ? (
                <small className="p-error">{errors.rut}</small>
              ) : rutChecking ? (
                <small className="block mt-1 text-xs text-color-secondary">
                  <i className="pi pi-spin pi-spinner mr-1" />
                  Verificando RUT...
                </small>
              ) : rutMessage ? (
                <small
                  className="block mt-1"
                  style={{
                    color: rutExists ? "var(--red-500)" : "var(--green-600)",
                  }}
                >
                  {rutMessage}
                </small>
              ) : rutStatus === "valid" ? (
                <small
                  className="block mt-1"
                  style={{ color: "var(--green-600)" }}
                >
                  RUT válido
                </small>
              ) : null}
            </div>
          )}

          {/* Descripción LEAD */}
          {personType === "lead" && (
            <div className="field col-12">
              <label className="block text-sm font-medium mb-2">
                Descripción LEAD <span className="text-red-500">*</span>
              </label>
              <InputText
                value={form.descripcionLead}
                onChange={(e) => setField("descripcionLead", e.target.value)}
                placeholder="Ej: Contacto de formulario web, interesado en producto X..."
                className={
                  errors.descripcionLead ? "p-invalid w-full" : "w-full"
                }
              />
              {errors.descripcionLead && (
                <small className="p-error">{errors.descripcionLead}</small>
              )}
            </div>
          )}

          <div className="field col-12">
            <label className="block text-sm font-medium mb-2">
              Etiqueta (opcional)
            </label>
            <InputText
              value={form.etiquetaPersona}
              onChange={(e) => setField("etiquetaPersona", e.target.value)}
              placeholder="Ej: Cliente VIP, Proveedor, Contacto interno…"
            />
          </div>
        </div>

        {/* Persona jurídica */}
        {personType === "juridica" && (
          <div className="mb-3">
            <label className="block text-sm font-medium mb-2">
              Razón social <span className="text-red-500">*</span>
            </label>
            <InputText
              value={form.razonSocial}
              onChange={(e) => setField("razonSocial", e.target.value)}
              placeholder="Ej: ACME S.A."
              className={errors.razonSocial ? "p-invalid" : ""}
            />
            {errors.razonSocial && (
              <small className="p-error">{errors.razonSocial}</small>
            )}
          </div>
        )}

        {/* Persona natural */}
        {personType === "natural" && (
          <>
            <div className="grid formgrid">
              <div className="field col-12 md:col-6">
                <label className="block text-sm font-medium mb-2">
                  Primer nombre <span className="text-red-500">*</span>
                </label>
                <InputText
                  value={form.primerNombre}
                  onChange={(e) => setField("primerNombre", e.target.value)}
                  className={errors.primerNombre ? "p-invalid" : ""}
                />
                {errors.primerNombre && (
                  <small className="p-error">{errors.primerNombre}</small>
                )}
              </div>
              <div className="field col-12 md:col-6">
                <label className="block text-sm font-medium mb-2">
                  Segundo nombre
                </label>
                <InputText
                  value={form.segundoNombre}
                  onChange={(e) => setField("segundoNombre", e.target.value)}
                />
              </div>

              <div className="field col-12 md:col-6">
                <label className="block text-sm font-medium mb-2">
                  Apellido paterno <span className="text-red-500">*</span>
                </label>
                <InputText
                  value={form.apellidoPaterno}
                  onChange={(e) => setField("apellidoPaterno", e.target.value)}
                  className={errors.apellidoPaterno ? "p-invalid" : ""}
                />
                {errors.apellidoPaterno && (
                  <small className="p-error">{errors.apellidoPaterno}</small>
                )}
              </div>
              <div className="field col-12 md:col-6">
                <label className="block text-sm font-medium mb-2">
                  Apellido materno <span className="text-red-500">*</span>
                </label>
                <InputText
                  value={form.apellidoMaterno}
                  onChange={(e) => setField("apellidoMaterno", e.target.value)}
                  className={errors.apellidoMaterno ? "p-invalid" : ""}
                />
                {errors.apellidoMaterno && (
                  <small className="p-error">{errors.apellidoMaterno}</small>
                )}
              </div>

              <div className="field col-12 md:col-6">
                <label className="block text-sm font-medium mb-2">
                  Fecha de nacimiento <span className="text-red-500">*</span>
                </label>
                <Calendar
                  value={form.fechaNacimiento}
                  onChange={(e) => setField("fechaNacimiento", e.value)}
                  showIcon
                  showButtonBar
                  dateFormat="dd/mm/yy"
                  placeholder="dd/mm/aaaa"
                  maxDate={today}
                  className={errors.fechaNacimiento ? "p-invalid" : ""}
                />
                {errors.fechaNacimiento && (
                  <small className="p-error">{errors.fechaNacimiento}</small>
                )}
              </div>

              <div className="field col-12 md:col-6">
                <label className="block text-sm font-medium mb-2">
                  Género <span className="text-red-500">*</span>
                </label>
                <Dropdown
                  value={form.genero}
                  onChange={(e) => setField("genero", e.value)}
                  options={generoOptions}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Seleccione"
                  className={errors.genero ? "p-invalid" : ""}
                />
                {errors.genero && (
                  <small className="p-error">{errors.genero}</small>
                )}
              </div>
            </div>
          </>
        )}

        {/* Botones para agregar info adicional */}
        {/* Datos adicionales opcionales */}
        <div className="mb-3">
          <label className="block text-sm font-medium mb-2">
            Datos adicionales (opcionales)
          </label>

          <div className="flex flex-wrap gap-2 text-sm">
            <span
              className={
                "cursor-pointer px-3 py-1 border-round-2xl inline-flex align-items-center gap-2 " +
                (showDireccion
                  ? "bg-primary text-white"
                  : "surface-200 text-color-secondary")
              }
              onClick={() => setShowDireccion((v) => !v)}
            >
              <i className="pi pi-map-marker text-xs" />
              <span>
                {showDireccion ? "Quitar dirección" : "Agregar dirección"}
              </span>
            </span>

            <span
              className={
                "cursor-pointer px-3 py-1 border-round-2xl inline-flex align-items-center gap-2 " +
                (showCorreo
                  ? "bg-primary text-white"
                  : "surface-200 text-color-secondary")
              }
              onClick={() => setShowCorreo((v) => !v)}
            >
              <i className="pi pi-envelope text-xs" />
              <span>{showCorreo ? "Quitar correo" : "Agregar correo"}</span>
            </span>

            <span
              className={
                "cursor-pointer px-3 py-1 border-round-2xl inline-flex align-items-center gap-2 " +
                (showTelefono
                  ? "bg-primary text-white"
                  : "surface-200 text-color-secondary")
              }
              onClick={() => setShowTelefono((v) => !v)}
            >
              <i className="pi pi-phone text-xs" />
              <span>
                {showTelefono ? "Quitar teléfono" : "Agregar teléfono"}
              </span>
            </span>
          </div>
        </div>

        {showDireccion && (
          <div className="mb-3">
            <h4 className="text-base font-medium mb-2">Dirección</h4>

            {/* Input único que dispara la búsqueda en Google */}
            <div className="field col-12">
              <label className="block text-sm font-medium mb-2">
                Buscar dirección
              </label>
              <InputText
                value={direccionQuery}
                onChange={(e) => {
                  setDireccionQuery(e.target.value);
                  setDireccionSeleccionada(null); // si cambia el texto, ya no vale la selección anterior
                }}
                placeholder="Ej: Agustinas 123, Santiago, Chile"
                className="w-full"
              />
              {direccionSeleccionada && (
                <small className="text-xs text-green-600 block mt-1">
                  Dirección seleccionada: {direccionSeleccionada.direccion}
                </small>
              )}
            </div>

            {/* Lista de sugerencias */}
            <div className="field col-12">
              {loadingDireccion && (
                <div className="flex items-center gap-2 text-sm text-color-secondary">
                  <i className="pi pi-spin pi-spinner" />
                  Buscando dirección...
                </div>
              )}

              {!loadingDireccion && direccionResultados.length > 0 && (
                <div className="surface-card border-1 border-200 border-round-lg mt-2 max-h-20rem overflow-auto">
                  {direccionResultados.map((d, idx) => (
                    <div
                      key={d.place_id || idx}
                      className={
                        "p-2 cursor-pointer flex flex-column hover:surface-100 " +
                        (direccionSeleccionada &&
                        direccionSeleccionada.place_id === d.place_id
                          ? "bg-primary text-white"
                          : "")
                      }
                      onClick={() => {
                        setDireccionSeleccionada(d);
                        setDireccionQuery(d.direccion || "");
                        setDireccionResultados([]); // ocultar lista luego de seleccionar
                      }}
                    >
                      <span className="text-sm font-medium">
                        {d.direccion || "Sin texto"}
                      </span>
                      <span className="text-xs text-color-secondary">
                        {[d.comuna, d.ciudad, d.region, d.pais]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {!loadingDireccion &&
                direccionQuery.trim() &&
                direccionResultados.length === 0 &&
                !direccionSeleccionada && (
                  <small className="text-xs text-color-secondary">
                    No hay resultados para la búsqueda actual.
                  </small>
                )}
            </div>
            <div className="field col-12">
              <label className="block text-sm font-medium mb-2">
                Tipo de dirección
              </label>
              <Dropdown
                value={tipoDireccionId}
                onChange={(e) => setTipoDireccionId(e.value)}
                options={tiposDireccion}
                optionLabel="label"
                optionValue="value"
                placeholder={
                  loadingTiposDireccion ? "Cargando..." : "Seleccione tipo"
                }
                disabled={loadingTiposDireccion || !tiposDireccion.length}
                className="w-full"
              />
            </div>
          </div>
        )}

        {showCorreo && (
          <div className="mb-3">
            <h4 className="text-base font-medium mb-2">Correo electrónico</h4>
            <div className="grid formgrid">
              <div className="field col-12 md:col-8">
                <label className="block text-sm font-medium mb-2">Correo</label>
                <InputText
                  value={emailForm.email}
                  onChange={(e) =>
                    setEmailForm((p) => ({ ...p, email: e.target.value }))
                  }
                  placeholder="nombre@dominio.com"
                />
              </div>
              <div className="field col-12 md:col-4">
                <label className="block text-sm font-medium mb-2">
                  Etiqueta
                </label>
                <InputText
                  value={emailForm.etiqueta}
                  onChange={(e) =>
                    setEmailForm((p) => ({ ...p, etiqueta: e.target.value }))
                  }
                  placeholder="Trabajo, Personal..."
                />
              </div>
            </div>
          </div>
        )}

        {showTelefono && (
          <div className="mb-3">
            <h4 className="text-base font-medium mb-2">Teléfono</h4>
            <div className="grid formgrid">
              {/* Código de país */}
              <div className="field col-12 md:col-4">
                <label className="block text-sm font-medium mb-2">
                  Código de país
                </label>
                <Dropdown
                  value={phoneCountryCode}
                  onChange={(e) => setPhoneCountryCode(e.value)}
                  options={phoneCodeOptions}
                  optionLabel="label"
                  optionValue="code"
                  placeholder="Seleccione"
                  itemTemplate={phoneCodeOptionTemplate}
                  valueTemplate={phoneCodeOptionTemplate}
                  className="w-full"
                />
              </div>

              {/* Número local */}
              <div className="field col-12 md:col-4">
                <label className="block text-sm font-medium mb-2">
                  Número (sin código)
                </label>
                <InputText
                  value={telForm.numero}
                  onChange={(e) =>
                    setTelForm((p) => ({ ...p, numero: e.target.value }))
                  }
                  placeholder="9 1234 5678"
                />
              </div>

              {/* Etiqueta */}
              <div className="field col-12 md:col-4">
                <label className="block text-sm font-medium mb-2">
                  Etiqueta
                </label>
                <InputText
                  value={telForm.etiqueta}
                  onChange={(e) =>
                    setTelForm((p) => ({ ...p, etiqueta: e.target.value }))
                  }
                  placeholder="Móvil, Fijo..."
                />
              </div>
            </div>

            <small className="text-xs text-color-secondary block mt-1">
              Se guardará como:{" "}
              {phoneCountryCode && telForm.numero
                ? `${phoneCountryCode} ${telForm.numero}`
                : "—"}
            </small>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default Contacto;
